import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addPosition } from "../../Redux/Actions/pozicioneActions";

const API_URL = "https://api.arkiva.gov.al/api/open-positions";

const AddOpenPosition = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedUser = useSelector((state) => state.users.loggedUser);
  const isLoggedIn = Object.keys(loggedUser || {}).length > 0;

  const [title, setTitle] = useState("");
  const [announceDate, setAnnounceDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [newId, setNewId] = useState(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingId, setIsLoadingId] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const generateSlug = (input) => {
    return input
      .toLowerCase()
      .trim()
      .replace(/ë/g, "e")
      .replace(/ç/g, "c")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const slug = useMemo(() => generateSlug(title), [title]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const controller = new AbortController();

    const fetchLatestId = async () => {
      try {
        setIsLoadingId(true);

        const response = await axios.get(`${API_URL}/latest-id`, {
          signal: controller.signal,
        });

        const latestId = Number(response.data.latestId) || 0;

        setNewId(latestId + 1);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }

        console.error("Error fetching latest position ID:", error);

        setNewId(null);

        setErrors((previousErrors) => ({
          ...previousErrors,
          submit:
            error.response?.data?.message ||
            "Nuk u gjenerua ID-ja e pozicionit. Rifreskoni faqen.",
        }));
      } finally {
        setIsLoadingId(false);
      }
    };

    fetchLatestId();

    return () => {
      controller.abort();
    };
  }, [isLoggedIn]);

  const clearError = (fieldName) => {
    setErrors((previousErrors) => {
      if (!previousErrors[fieldName]) {
        return previousErrors;
      }

      const updatedErrors = { ...previousErrors };
      delete updatedErrors[fieldName];

      return updatedErrors;
    });
  };

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    clearError("title");
  };

  const handleAnnounceDateChange = (event) => {
    setAnnounceDate(event.target.value);
    clearError("announceDate");
  };

  const handleFinalDateChange = (event) => {
    setFinalDate(event.target.value);
    clearError("finalDate");
  };

  const handleLinkChange = (event) => {
    setLink(event.target.value);
    clearError("link");
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    setFile(selectedFile);
    clearError("file");
  };

  const previewUrl = useMemo(() => {
    if (!file) {
      return "";
    }

    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const isValidDate = (dateValue) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);

    if (!match) {
      return false;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);

    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const parseDate = (dateValue) => {
    const [day, month, year] = dateValue.split("/").map(Number);

    return new Date(year, month - 1, day);
  };

  const isValidUrl = (value) => {
    if (!value) {
      return true;
    }

    try {
      const parsedUrl = new URL(value);

      return ["http:", "https:"].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  };

  const validate = () => {
    const validationErrors = {};

    const trimmedTitle = title.trim();
    const trimmedAnnounceDate = announceDate.trim();
    const trimmedFinalDate = finalDate.trim();
    const trimmedLink = link.trim();

    if (!trimmedTitle) {
      validationErrors.title = "Titulli është i detyrueshëm.";
    }

    if (!slug) {
      validationErrors.title =
        "Titulli duhet të përmbajë karaktere të vlefshme.";
    }

    if (trimmedFinalDate && !isValidDate(trimmedFinalDate)) {
      validationErrors.finalDate =
        "Data e përfundimit duhet të jetë në formatin dd/mm/yyyy.";
    }

    if (!trimmedAnnounceDate) {
      validationErrors.announceDate = "Data e shpalljes është e detyrueshme.";
    }

    if (isValidDate(trimmedAnnounceDate) && isValidDate(trimmedFinalDate)) {
      const announce = parseDate(trimmedAnnounceDate);
      const final = parseDate(trimmedFinalDate);

      if (final < announce) {
        validationErrors.finalDate =
          "Data e përfundimit nuk mund të jetë para datës së shpalljes.";
      }
    }

    if (trimmedLink && !isValidUrl(trimmedLink)) {
      validationErrors.link =
        "Vendosni një link të vlefshëm që fillon me http:// ose https://.";
    }

    if (file) {
      const extension = file.name.split(".").pop()?.toLowerCase();

      if (extension !== "pdf") {
        validationErrors.file = "Lejohen vetëm dokumente PDF.";
      }

      const maxFileSize = 20 * 1024 * 1024;

      if (file.size > maxFileSize) {
        validationErrors.file =
          "Dokumenti nuk mund të jetë më i madh se 20 MB.";
      }
    }

    if (newId === null) {
      validationErrors.submit =
        "Nuk u gjenerua ID-ja e pozicionit. Rifreskoni faqen.";
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting || !validate()) {
      return;
    }

    const formData = new FormData();

    formData.append("title", title.trim());
    formData.append("announceDate", announceDate.trim());
    formData.append("finalDate", finalDate.trim());
    formData.append("slug", slug);
    formData.append("id", String(newId));

    if (link.trim()) {
      formData.append("link", link.trim());
    }

    if (file) {
      formData.append("file", file, file.name);
    }

    try {
      setIsSubmitting(true);
      setErrors({});

      const response = await axios.post(`${API_URL}/create`, formData);

      dispatch(addPosition(response.data));

      navigate("/manage/pozicione-vakante", {
        replace: true,
      });
    } catch (error) {
      console.error("Error creating open position:", error);

      setErrors({
        submit:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Ndodhi një gabim gjatë shtimit të pozicionit vakant.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/manage/pozicione-vakante");
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="edit-page">
      <div className="return">
        <button type="button" onClick={handleCancel}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
      </div>

      <p>Shto Pozicion Vakant</p>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-group">
          <label htmlFor="title">Titulli</label>

          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={handleTitleChange}
            disabled={isSubmitting}
          />

          {errors.title && <small className="error">{errors.title}</small>}
        </div>

        <div className="form-group">
          <label htmlFor="link">Linku</label>

          <input
            type="url"
            id="link"
            name="link"
            value={link}
            placeholder="https://example.com"
            onChange={handleLinkChange}
            disabled={isSubmitting}
          />

          {errors.link && <small className="error">{errors.link}</small>}
        </div>

        <div className="form-group">
          <label htmlFor="file">Dokumenti</label>

          <input
            type="file"
            id="file"
            name="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            disabled={isSubmitting}
          />

          {file && (
            <small className="selected-file">Dokumenti: {file.name}</small>
          )}

          {errors.file && <small className="error">{errors.file}</small>}
        </div>

        {file && previewUrl && (
          <div className="form-group">
            <label>Paraqitja e dokumentit</label>

            <div className="pdf-preview">
              <iframe
                height={500}
                width={650}
                src={previewUrl}
                title={`Paraqitja e dokumentit ${file.name}`}
                className="pdf-preview-frame"
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="announceDate">Data e shpalljes</label>

          <input
            type="text"
            id="announceDate"
            name="announceDate"
            value={announceDate}
            placeholder="dd/mm/yyyy"
            maxLength={10}
            onChange={handleAnnounceDateChange}
            disabled={isSubmitting}
          />

          {errors.announceDate && (
            <small className="error">{errors.announceDate}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="finalDate">Data e përfundimit</label>

          <input
            type="text"
            id="finalDate"
            name="finalDate"
            value={finalDate}
            placeholder="dd/mm/yyyy"
            maxLength={10}
            onChange={handleFinalDateChange}
            disabled={isSubmitting}
          />

          {errors.finalDate && (
            <small className="error">{errors.finalDate}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="slug">Slug</label>

          <input type="text" id="slug" value={slug} readOnly />
        </div>

        <div className="form-group">
          <label htmlFor="positionId">ID</label>

          <input
            type="text"
            id="positionId"
            value={isLoadingId ? "Duke u gjeneruar..." : (newId ?? "")}
            readOnly
          />
        </div>

        {errors.submit && (
          <div className="error submit-error">{errors.submit}</div>
        )}

        <div className="edit-actions">
          <button
            type="submit"
            className="save"
            disabled={isSubmitting || isLoadingId || newId === null}
          >
            {isSubmitting ? "Duke shtuar..." : "Shto Pozicion Vakant"}
          </button>

          <button
            type="button"
            className="cancel"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Anullo
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddOpenPosition;
